import type { Swiper as SwiperClass } from 'swiper/types';

import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { clsx } from 'clsx';
import Image from 'next/image';

import { useComparison } from '@screens/Comparison';
import { PRODUCT_IMAGE_PATH } from '@shared/config/paths/paths';
import { ICON_DEFAULT } from '@shared/constants/colors';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';
import { Container } from '@shared/ui/Container';
import { getScreenWidth } from '@shared/ui/ScreenSizeTracking';
import { formatPrice } from '@shared/utils/formatPrice/formatPrice';
import { getProductCardData } from '@shared/utils/getProductCardData/getProductCardData';

import { getSlidesQuantity } from '../../../utils/getSlidesQuantity/getSlidesQuantity';
import cls from './FixedSlider.module.scss';

type FixedSliderProps = {
  mainSlider: SwiperClass | null | undefined;
  fixedSlider: SwiperClass | null | undefined;
  setFixedSlider: (api: SwiperClass) => void;
};

export const FixedSlider = ({ mainSlider, setFixedSlider, fixedSlider }: FixedSliderProps) => {
  const dispatch = useAppDispatch();
  const width = useAppSelector(getScreenWidth);

  const {
    products,
    activeCategory,
    controlSlider,
    fixedSlider: sliderVisible,
    showFixedSlider,
    changeSlide
  } = useComparison();

  const productsForCategory = useMemo(() => getProductCardData(products[activeCategory]), [activeCategory, products]);
  const minQuantitySlides = Object.values(products[activeCategory])?.length > getSlidesQuantity();

  const nextSlideHandler = () => {
    fixedSlider?.slideNext();
    mainSlider?.slideNext();
  };

  const prevSlideHandler = () => {
    fixedSlider?.slidePrev();
    mainSlider?.slidePrev();
  };

  useEffect(() => {
    const scrollHandler = () => {
      const scrollDistance = window.scrollY;

      showFixedSlider(scrollDistance > 600);
    };

    window.addEventListener('scroll', scrollHandler);

    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [dispatch, showFixedSlider]);

  return (
    <div className={clsx(cls.wrap, { [cls.visible]: sliderVisible })}>
      <Container className={cls.container}>
        {controlSlider.prev && (
          <button
            onClick={prevSlideHandler}
            className={cls.prev}
            aria-label={'Предыдущий слайд с товарами сравнения'}
          >
            <ArrowLeft
              color={ICON_DEFAULT}
              size={16}
            />
          </button>
        )}

        <Swiper
          className={cls.slider}
          spaceBetween={12}
          slidesPerView={2}
          onSwiper={setFixedSlider}
          onResize={(api) => {
            if (width < 1281) {
              api.allowTouchMove = true;
            }
          }}
          breakpoints={{
            768: {
              slidesPerView: 3
            },
            1081: {
              slidesPerView: 4
            }
          }}
          onSlideChange={(api) => {
            changeSlide(api.realIndex, minQuantitySlides && !api.isBeginning, minQuantitySlides && !api.isEnd);
          }}
        >
          {productsForCategory.map((el) => (
            <SwiperSlide key={el.id}>
              <div className={cls.card}>
                <div className={cls.flex}>
                  <div className={cls.image}>
                    {el.images && el.images.length > 0 ? (
                      <Image
                        fill
                        src={`${PRODUCT_IMAGE_PATH}/${el.images[0].value}/400.webp`}
                        alt={''}
                      />
                    ) : (
                      <div className={cls.empty}></div>
                    )}
                  </div>
                  <div className={cls.block}>
                    <div className={cls.price}>{formatPrice(el.price)}</div>
                    <div className={cls.name}>{el.name}</div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        {controlSlider.next && (
          <button
            onClick={nextSlideHandler}
            className={cls.next}
            aria-label={'Следующий слайд с товарами сравнения'}
          >
            <ArrowRight
              size={16}
              color={ICON_DEFAULT}
            />
          </button>
        )}
      </Container>
    </div>
  );
};
